import { useEffect } from 'react';
import { useLocation, useParams } from 'wouter';
import { useAppointmentDetail, useTransitionAppointment } from '@/hooks/useScheduling';
import { AppointmentStatusBadge } from '@/components/scheduling/AppointmentStatusBadge';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { SCHEDULING_PERMISSIONS, hasPermission } from '@/lib/schedulingPermissions';

const SERVICE_TYPE_LABELS: Record<string, string> = {
  consultation: '咨询',
  infusion: '输液',
  followup: '随访',
  assessment: '评估',
  other: '其他',
};

export default function AppointmentDetailPage() {
  const [, navigate] = useLocation();
  const params = useParams<{ appointmentId: string }>();
  const { permissions } = useAuth();
  const { appointment, loading, error, fetchAppointment } = useAppointmentDetail();
  const {
    confirmAppointment,
    cancelAppointment,
    checkInAppointment,
    completeAppointment,
    noShowAppointment,
    loading: actionLoading,
    error: actionError,
  } = useTransitionAppointment();

  // Derive permission flags — must be before any conditional returns
  const canRead = hasPermission(permissions, SCHEDULING_PERMISSIONS.APPOINTMENT_READ);
  const canUpdate = hasPermission(permissions, SCHEDULING_PERMISSIONS.APPOINTMENT_UPDATE);
  const canCancel = hasPermission(permissions, SCHEDULING_PERMISSIONS.APPOINTMENT_CANCEL);

  useEffect(() => {
    if (!canRead) return;
    if (params.appointmentId) {
      void fetchAppointment(params.appointmentId);
    }
  }, [params.appointmentId, fetchAppointment, canRead]);

  // Permission early return — after all hooks
  if (!canRead) {
    return <AccessDenied resource="预约详情" />;
  }

  async function handleAction(action: 'confirm' | 'cancel' | 'check_in' | 'complete' | 'no_show') {
    if (!appointment) return;
    const dto = { expectedVersion: appointment.version };
    let result = null;
    switch (action) {
      case 'confirm':
        result = await confirmAppointment(appointment.id, dto);
        break;
      case 'cancel':
        if (!confirm('确认取消该预约？')) return;
        result = await cancelAppointment(appointment.id, dto);
        break;
      case 'check_in':
        result = await checkInAppointment(appointment.id, dto);
        break;
      case 'complete':
        result = await completeAppointment(appointment.id, dto);
        break;
      case 'no_show':
        result = await noShowAppointment(appointment.id, dto);
        break;
    }
    if (result && params.appointmentId) {
      void fetchAppointment(params.appointmentId);
    }
  }

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">加载中...</div>;
  }

  if (error) {
    return (
      <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
        加载失败：{error.message}
      </div>
    );
  }

  if (!appointment) {
    return <div className="text-center py-8 text-muted-foreground">预约不存在</div>;
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/scheduling/appointments')}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          ← 返回列表
        </button>
        <h1 className="text-2xl font-semibold">预约详情</h1>
      </div>

      {/* 409 conflict or action error */}
      {actionError && (
        <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
          操作失败：{actionError.message}
        </div>
      )}

      <div className="rounded-md border p-6 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground font-mono">{appointment.id}</span>
          <AppointmentStatusBadge status={appointment.status} />
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-muted-foreground mb-1">客户 ID</div>
            <div className="font-mono">{appointment.customerId}</div>
          </div>
          <div>
            <div className="text-muted-foreground mb-1">服务类型</div>
            <div>{SERVICE_TYPE_LABELS[appointment.serviceType] ?? appointment.serviceType}</div>
          </div>
          <div>
            <div className="text-muted-foreground mb-1">开始时间</div>
            <div>
              {new Date(appointment.startAt).toLocaleString('zh-CN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground mb-1">结束时间</div>
            <div>
              {new Date(appointment.endAt).toLocaleString('zh-CN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground mb-1">负责员工</div>
            <div className="font-mono">{appointment.assignedStaffId}</div>
          </div>
          <div>
            <div className="text-muted-foreground mb-1">版本</div>
            <div>{appointment.version}</div>
          </div>
          {appointment.notes && (
            <div className="col-span-2">
              <div className="text-muted-foreground mb-1">备注</div>
              <div>{appointment.notes}</div>
            </div>
          )}
          <div>
            <div className="text-muted-foreground mb-1">创建时间</div>
            <div>
              {new Date(appointment.createdAt).toLocaleString('zh-CN')}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground mb-1">更新时间</div>
            <div>
              {new Date(appointment.updatedAt).toLocaleString('zh-CN')}
            </div>
          </div>
        </div>
      </div>

      {/* Action buttons — gated by permissions */}
      <div className="flex flex-wrap gap-3">
        {canUpdate && appointment.status === 'pending' && (
          <button
            onClick={() => void handleAction('confirm')}
            disabled={actionLoading}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            确认预约
          </button>
        )}
        {canUpdate && appointment.status === 'confirmed' && (
          <>
            <button
              onClick={() => void handleAction('check_in')}
              disabled={actionLoading}
              className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
            >
              到店确认
            </button>
            <button
              onClick={() => void handleAction('no_show')}
              disabled={actionLoading}
              className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
            >
              标记未到店
            </button>
          </>
        )}
        {canUpdate && appointment.status === 'checked_in' && (
          <button
            onClick={() => void handleAction('complete')}
            disabled={actionLoading}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            完成服务
          </button>
        )}
        {canCancel && (appointment.status === 'pending' || appointment.status === 'confirmed') && (
          <button
            onClick={() => void handleAction('cancel')}
            disabled={actionLoading}
            className="rounded-md border border-destructive px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50"
          >
            取消预约
          </button>
        )}
      </div>
    </div>
  );
}
